import { useMemo } from "react";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SEO } from "@/components/seo/SEO";
import { usePublicReferences } from "@/features/references/hooks";
import type { ReferenceEntry } from "@/features/references/types";
import { PublicError, PublicLoading } from "@/pages/public/PublicState";
import { ReferenceGrid } from "./ReferenceGrid";

function sortReferences(references: ReferenceEntry[]) {
  return [...references].sort((a, b) => {
    if (a.featured !== b.featured) return a.featured ? -1 : 1;
    return a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);
  });
}

export function ReferencesPage() {
  const query = usePublicReferences();
  const references = useMemo(() => sortReferences(query.data ?? []), [query.data]);
  const featuredCount = references.filter((reference) => reference.featured).length;

  if (query.isLoading) return <PublicLoading label="Loading references..." />;
  if (query.isError) return <PublicError label="Unable to load references." />;

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <SEO title="References" description="Published client testimonials and references for Kalaco." />
      <CyberPanel>
        <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-teal-200 sm:text-sm sm:tracking-[0.28em]">References</p>
            <h1 className="mt-3 break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl">
              Client Testimonials
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-content-secondary sm:text-lg sm:leading-8">
              Published references from the CMS, with featured testimonials shown first.
            </p>
          </div>
          <div className="w-full shrink-0 rounded border border-cyan-300/20 bg-black/40 px-4 py-3 text-sm text-content-secondary lg:w-auto">
            <span className="font-bold text-cyan-100">{featuredCount}</span> featured / {references.length} active
          </div>
        </div>
      </CyberPanel>

      <div className="mt-8">
        <ReferenceGrid references={references} />
      </div>
    </section>
  );
}
